// ============================================================
// TMR Portal Admin — aplicar uma taxa temporária (com a cascata para os lançamentos)
// ============================================================
import { useState } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn } from '../../components/primitives';
import { ModalShell } from '../../components/ModalShell';
import { Notice } from '../../components/Notice';
import { TextField } from '../../components/fields';
import { Icons } from '../../lib/icons';
import { bandLabel, formatRateValue, rateUnitSuffix } from '../../data/rateFormat';
import { useApplyTemporaryRate } from '../../data/rates/queries';
import {
  toApplyTemporaryRequest,
  validateTemporaryRateForm,
} from '../../data/rates/form';
import type { TemporaryRateForm, TemporaryRateFormErrors } from '../../data/rates/form';
import { cascadeTargets, cascades, rateTitle, todayIso } from '../../data/rates/status';
import type { CompanyRate } from '../../data/rates/contract';

/** Props for {@link TemporaryRateModal}. */
interface TemporaryRateModalProps {
  companyId: string;
  /** The rate the operator clicked on. */
  rate: CompanyRate;
  /** Every rate of the Empresa — needed to count what the cascade would move. */
  all: readonly CompanyRate[];
  onClose: () => void;
}

/**
 * Applies a temporary rate to a rate, and by default to the Empresa's Launch rates that match it.
 *
 * The cascade checkbox is the important control on this form, and the copy says why rather than leaving it
 * as a toggle: the legacy calculates from the *product's* rate row, so a temporary rate applied only to the
 * Empresa's row is recorded, replicated, shown on this screen — and charged to nobody (`08` §8). Turning it
 * off is a legitimate choice, and the number of rates it would move is spelled out so it is an informed one.
 */
export function TemporaryRateModal({ companyId, rate, all, onClose }: TemporaryRateModalProps) {
  const [form, setForm] = useState<TemporaryRateForm>(() => ({
    value: '',
    startDate: todayIso(),
    endDate: '',
    reason: '',
    cascade: true,
  }));
  const [errors, setErrors] = useState<TemporaryRateFormErrors>({});

  const apply = useApplyTemporaryRate(companyId);
  const targets = cascadeTargets(rate, all);
  const canCascade = cascades(rate);

  const set = <K extends keyof TemporaryRateForm>(key: K, value: TemporaryRateForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const submit = async () => {
    const found = validateTemporaryRateForm(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    await apply.mutateAsync({ rateId: rate.id, request: toApplyTemporaryRequest(form) });
    onClose();
  };

  return (
    <ModalShell
      title="Aplicar taxa temporária"
      lead={`${rateTitle(rate)} · ${bandLabel(rate)} · taxa padrão de ${formatRateValue(rate.standardValue, rate.rateTypeId)}`}
      onClose={onClose}
    >
      <div style={{ display: 'grid', gap: 14 }}>
        <TextField
          label="Taxa temporária"
          required
          inputMode="decimal"
          value={form.value}
          onChange={(value) => set('value', value)}
          error={errors.value}
          suffix={rateUnitSuffix(rate.rateTypeId)}
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <TextField
            label="Início"
            required
            type="date"
            value={form.startDate}
            onChange={(value) => set('startDate', value)}
            error={errors.startDate}
          />
          <TextField
            label="Término"
            required
            type="date"
            value={form.endDate}
            onChange={(value) => set('endDate', value)}
            error={errors.endDate}
          />
        </div>

        <TextField
          label="Motivo (opcional)"
          value={form.reason}
          onChange={(value) => set('reason', value)}
          placeholder="Ex.: campanha comercial de julho"
          maxLength={500}
        />

        {canCascade && (
          <label
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
              padding: '12px 14px',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={form.cascade}
              onChange={(event) => set('cascade', event.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span style={{ fontSize: 13.5, lineHeight: 1.5 }}>
              <strong>Aplicar também aos lançamentos</strong>
              <br />
              <span style={{ color: 'var(--fg-muted)' }}>
                {targets.length === 0
                  ? 'Esta empresa ainda não tem taxa de lançamento com este tipo e faixa.'
                  : targets.length === 1
                    ? 'Uma taxa de lançamento com o mesmo tipo e faixa recebe a mesma janela.'
                    : `${targets.length} taxas de lançamento com o mesmo tipo e faixa recebem a mesma janela.`}
              </span>
            </span>
          </label>
        )}

        {/* Not a footnote: without the cascade the exception exists everywhere except where the money is
            calculated. The operator has to be able to make that choice knowing it. */}
        {canCascade && !form.cascade && targets.length > 0 && (
          <Notice tone="warning">
            Sem aplicar aos lançamentos, a taxa temporária vale para a empresa mas os pedidos dos produtos
            continuam sendo cobrados pela taxa padrão.
          </Notice>
        )}

        {apply.error && (
          <Notice tone="danger">
            {apply.error instanceof ApiError
              ? apply.error.detail
              : 'Não foi possível aplicar a taxa temporária.'}
          </Notice>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
        <Btn variant="secondary" onClick={onClose} disabled={apply.isPending}>
          Cancelar
        </Btn>
        <Btn variant="primary" icon={Icons.clock} onClick={() => void submit()} disabled={apply.isPending}>
          {apply.isPending ? 'Aplicando…' : 'Aplicar taxa temporária'}
        </Btn>
      </div>
    </ModalShell>
  );
}
